import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { MediaAsset } from '@neuroedge/shared';
import { apiClient, type ApiClientError } from './client';

/** Uploaded images (`/media-assets`) - product/service photos. */

export const uploadMediaAsset = (image: Blob) => {
  const form = new FormData();
  form.append('file', image, image.type === 'image/png' ? 'image.png' : 'image.jpg');
  return apiClient.post<MediaAsset>('/media-assets', form);
};

export function useUploadMediaAsset() {
  return useMutation<MediaAsset, ApiClientError, Blob>({ mutationFn: uploadMediaAsset });
}

/**
 * An object URL for an uploaded image. The content route is authenticated,
 * so a plain `<img src>` can't load it - fetch it as a blob instead. An
 * asset's bytes never change (a new upload is a new id), so it's cached for
 * the whole session.
 */
export function useMediaAssetUrl(assetId: string | null | undefined): string | null {
  const blob = useQuery<Blob, ApiClientError>({
    queryKey: ['media-assets', assetId, 'content'],
    queryFn: async () => (await apiClient.getBlob(`/media-assets/${assetId}/content`)).blob,
    enabled: Boolean(assetId),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob.data) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob.data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob.data]);
  return assetId ? url : null;
}
